import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  UNCLASSIFIED_COMPANY,
  DOCUMENT_TYPE_LABEL_HU,
  DocumentType,
  SPINE_CURRENCY_LABEL_HU,
  SPINE_FIELD_NAMES,
  SpineCurrency,
  type FolderDetail,
  type SpineFieldName,
} from '@persped/shared';
import { useState } from 'react';
import { useCompanies, useUpdateSpineFields, type UpdateSpineFieldsVars } from '../../api/queries';
import { ProblemAlert } from '../../components/ProblemAlert';

/** Magyar mezőnevek — a `Record<…>` típus miatt új mező nélkülük nem vezethető be. */
const FIELD_LABEL_HU: Record<SpineFieldName, string> = {
  company: 'Cég',
  documentType: 'Irattípus',
  currency: 'Pénznem',
  yearFrom: 'Év (-tól)',
  yearTo: 'Év (-ig)',
  periodFrom: 'Időszak (-tól)',
  periodTo: 'Időszak (-ig)',
  numberFrom: 'Tartomány (-tól)',
  numberTo: 'Tartomány (-ig)',
  volume: 'Kötet',
};

/** A szerkesztő minden mezőt SZÖVEGKÉNT tart — a konverzió a mentésnél történik. */
type FormState = Record<SpineFieldName, string>;

function toForm(spine: FolderDetail['spine']): FormState {
  return {
    company: spine.company,
    documentType: spine.documentType ?? '',
    currency: spine.currency ?? '',
    yearFrom: spine.yearFrom?.toString() ?? '',
    yearTo: spine.yearTo?.toString() ?? '',
    periodFrom: spine.periodFrom ?? '',
    periodTo: spine.periodTo ?? '',
    numberFrom: spine.numberFrom ?? '',
    numberTo: spine.numberTo ?? '',
    volume: spine.volume?.toString() ?? '',
  };
}

/**
 * A gerincadatok kézi javítása (E4, 08-12 findings 2. pont).
 *
 * **Ez nem sérti a 2. alapelvet.** A gerincSZÖVEGET az `EditTextCard` írja, és
 * az marad az egyetlen írója; itt a szövegből SZÁRMAZTATOTT mezők javíthatók.
 * Mindkettő emberi művelet — a felismerés egyiket sem írja.
 *
 * A javított mezőt a következő szövegmentés újraszámolása NEM bántja
 * (`spine_manual_fields`). A „Vissza az automatikusra" ezt oldja fel — enélkül
 * egy elrontott javítás véglegesen befagyna.
 *
 * A `key={folder.rowVersion}` a szülőben teremti újra a komponenst mentés
 * után, így az űrlap mindig a friss szerveradatból indul, effektbeli setState
 * nélkül — ugyanaz a minta, mint az `EditLocationCard`-nál.
 */
export function SpineFieldsCard({ folder }: { folder: FolderDetail }) {
  const update = useUpdateSpineFields();
  // Csak AKTÍV cégek választhatók újonnan; egy régi, deaktivált besorolás a
  // mappán megmarad, de nem kínáljuk fel újra.
  const companies = useCompanies();
  const [form, setForm] = useState<FormState>(() => toForm(folder.spine));

  const set = (field: SpineFieldName, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const eredeti = toForm(folder.spine);
  const valtozott = SPINE_FIELD_NAMES.filter((f) => form[f] !== eredeti[f]);
  const manual = new Set(folder.spine.manualFields);

  const submit = () => {
    // Csak a MEGVÁLTOZOTT mezőket küldjük: a hiányzó mező jelentése „ne nyúlj
    // hozzá", tehát egy változatlan mező elküldése fölöslegesen tenné kézivé.
    const vars: UpdateSpineFieldsVars = { barcode: folder.barcode, rowVersion: folder.rowVersion };
    for (const f of valtozott) {
      const raw = form[f].trim();
      switch (f) {
        // A cég SOSEM üres — ha kiürítik, `Besorolatlan` lesz, ahogy a
        // parser is teszi.
        case 'company':
          vars.company = raw || UNCLASSIFIED_COMPANY;
          break;
        case 'documentType':
          vars.documentType = raw === '' ? null : (raw as DocumentType);
          break;
        case 'currency':
          vars.currency = raw === '' ? null : (raw as SpineCurrency);
          break;
        case 'yearFrom':
        case 'yearTo':
        case 'volume':
          vars[f] = raw === '' ? null : Number(raw);
          break;
        default:
          vars[f] = raw === '' ? null : raw;
      }
    }
    update.mutate(vars);
  };

  const reset = (field: SpineFieldName) =>
    update.mutate({
      barcode: folder.barcode,
      rowVersion: folder.rowVersion,
      resetFields: [field],
    });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle1">Gerincadatok szerkesztése</Typography>

        <Alert severity="info">
          Ezek a mezők a gerincszövegből számolódnak. Amit itt kézzel megadsz, azt a szöveg
          következő mentése <strong>nem írja felül</strong> — amíg vissza nem adod az
          automatikának.
        </Alert>

        {update.isError && <ProblemAlert error={update.error} />}

        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
          <FieldWrapper field="company" manual={manual}>
            <TextField
              select
              label={FIELD_LABEL_HU.company}
              value={form.company}
              onChange={(e) => set('company', e.target.value)}
              sx={{ minWidth: 200 }}
            >
              {(companies.data ?? [])
                .filter((c) => c.isActive || c.code === form.company)
                .map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.name}
                  </MenuItem>
                ))}
            </TextField>
          </FieldWrapper>

          <FieldWrapper field="documentType" manual={manual}>
            <TextField
              select
              label={FIELD_LABEL_HU.documentType}
              value={form.documentType}
              onChange={(e) => set('documentType', e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">—</MenuItem>
              {Object.values(DocumentType).map((d) => (
                <MenuItem key={d} value={d}>
                  {DOCUMENT_TYPE_LABEL_HU[d]}
                </MenuItem>
              ))}
            </TextField>
          </FieldWrapper>

          <FieldWrapper field="currency" manual={manual}>
            <TextField
              select
              label={FIELD_LABEL_HU.currency}
              value={form.currency}
              onChange={(e) => set('currency', e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">—</MenuItem>
              {Object.values(SpineCurrency).map((c) => (
                <MenuItem key={c} value={c}>
                  {SPINE_CURRENCY_LABEL_HU[c]}
                </MenuItem>
              ))}
            </TextField>
          </FieldWrapper>

          {(['yearFrom', 'yearTo', 'periodFrom', 'periodTo', 'numberFrom', 'numberTo', 'volume'] as const).map(
            (field) => (
              <FieldWrapper key={field} field={field} manual={manual}>
                <TextField
                  label={FIELD_LABEL_HU[field]}
                  value={form[field]}
                  onChange={(e) => set(field, e.target.value)}
                  helperText={
                    field === 'periodFrom' || field === 'periodTo' ? 'MM vagy MM-DD' : undefined
                  }
                  sx={{ minWidth: 150 }}
                />
              </FieldWrapper>
            ),
          )}
        </Stack>

        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            disabled={update.isPending || valtozott.length === 0}
            onClick={submit}
          >
            Mentés ({valtozott.length} mező)
          </Button>

          {[...manual].map((field) => (
            <Button
              key={field}
              size="small"
              disabled={update.isPending}
              onClick={() => reset(field as SpineFieldName)}
            >
              „{FIELD_LABEL_HU[field as SpineFieldName]}" vissza az automatikusra
            </Button>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
}

/** A kézzel felülbírált mezőt jelöljük — annak más a jelentése. */
function FieldWrapper({
  field,
  manual,
  children,
}: {
  field: SpineFieldName;
  manual: Set<string>;
  children: React.ReactNode;
}) {
  return (
    <Stack spacing={0.5}>
      {children}
      {manual.has(field) && <Chip size="small" color="primary" variant="outlined" label="kézi" />}
    </Stack>
  );
}
